import { type BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react'
import type { AxiosError, AxiosRequestConfig } from 'axios'
import { createAxiosInstance } from '../../api'
import {
  OrganizationWorkData,
  SearchWorksParams,
  WorkCreationRequest,
  WorkData,
  WorkListPage,
  WorkProductAttachmentData,
  WorkUpdateData,
} from '../../types'

export const apiWorkBaseQuery =
  (): BaseQueryFn<
    {
      url: string
      method: AxiosRequestConfig['method']
      data?: AxiosRequestConfig['data']
      params?: AxiosRequestConfig['params']
      headers?: AxiosRequestConfig['headers']
    },
    unknown,
    unknown
  > =>
  async ({ url, method, data, params, headers }) => {
    const api = createAxiosInstance(`${String(process.env.REACT_APP_ENGINE_API_URL)}/reporting/work-products`)

    try {
      const result = await api(url, {
        method,
        data,
        params,
      })
      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError
      console.error(err)

      throw axiosError
    }
  }

export const workApi = createApi({
  reducerPath: 'workApi',
  baseQuery: apiWorkBaseQuery(),
  endpoints: (builder) => ({
    getWork: builder.query<OrganizationWorkData, string>({
      query(workId: string) {
        return {
          url: `/${workId}`,
          method: 'get',
        }
      },
    }),
    getWorkAttachments: builder.query<WorkProductAttachmentData[], string>({
      query(workId: string) {
        return {
          url: `/${workId}/attachment`,
          method: 'get',
        }
      },
    }),
    updateWork: builder.mutation<OrganizationWorkData, { data: WorkUpdateData; workId: string }>({
      query({ data, workId }) {
        return {
          url: `/${workId}`,
          method: 'put',
          data,
        }
      },
    }),
    searchWorks: builder.mutation<WorkListPage, { data: SearchWorksParams }>({
      query({ data }) {
        return {
          url: 'search',
          method: 'post',
          data,
        }
      },
    }),
    addWork: builder.mutation<WorkData, { data: WorkCreationRequest }>({
      query({ data }) {
        return {
          url: '',
          method: 'post',
          data,
        }
      },
    }),
    deleteWork: builder.mutation<any, string>({
      query(workId) {
        return {
          url: `/${workId}`,
          method: 'delete',
        }
      },
    }),
    uploadWorkAttachment: builder.mutation<WorkProductAttachmentData, { workId: string; data: FormData }>({
      query({ workId, data }) {
        return {
          url: `/${workId}/attachment`,
          method: 'post',
          data,
          formData: true,
        }
      },
    }),
  }),
})

export const {
  useSearchWorksMutation,
  useUpdateWorkMutation,
  useLazyGetWorkQuery,
  useAddWorkMutation,
  useDeleteWorkMutation,
  useUploadWorkAttachmentMutation,
  useLazyGetWorkAttachmentsQuery,
} = workApi
